import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ConversationReplayDTO } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";

type Message = ConversationReplayDTO["messages"][number];

/** How often to re-fetch while a conversation is still live. */
const LIVE_POLL_MS = 3000;

/** A conversation still receiving turns — poll it; a finished one is static. */
function isLive(d: ConversationReplayDTO): boolean {
  return d.status === "active" && !d.completed;
}

/** Human-friendly label for a transition reason (debug display). */
function transitionReasonLabel(reason: string | undefined | null): string {
  switch (reason) {
    case "action_success":
      return "action succeeded";
    case "slot_condition":
      return "condition matched";
    case "slots_complete":
      return "info complete";
    case "no_progress":
      return "no progress → escape";
    case "stay":
      return "stayed";
    default:
      return reason ?? "";
  }
}

/** Chip text for a transition: the actual condition for slot_condition, else the reason. */
function transitionChipText(reason?: string | null, detail?: string | null): string {
  if (reason === "slot_condition" && detail) return `if ${detail}`;
  return transitionReasonLabel(reason);
}

/**
 * Per-conversation replay. Read-only chat-bubble transcript: customer
 * right, agent left, tool calls as collapsible inset cards. The api
 * enforces tenant ownership server-side — the client renders what it's
 * given. Spec: docs/conversation-replay-ux-spec.md
 */
export function ConversationReplayPage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; data: ConversationReplayDTO }
    | { status: "error"; message: string }
  >({ status: "loading" });
  // Whether we're actively polling (conversation still in progress).
  const [live, setLive] = useState(false);
  const endRef = useRef<HTMLLIElement>(null);
  const prevCount = useRef(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let loaded = false; // first successful load done? (skeleton only before it)

    const tick = async (): Promise<void> => {
      try {
        const data = await api.agentflow.getConversation(id);
        if (cancelled) return;
        loaded = true;
        setState({ status: "ready", data });
        // Keep polling only while the conversation is still receiving turns.
        if (isLive(data)) {
          setLive(true);
          timer = setTimeout(() => void tick(), LIVE_POLL_MS);
        } else {
          setLive(false);
        }
      } catch (e) {
        if (cancelled) return;
        if (!loaded) {
          setState({
            status: "error",
            message:
              e instanceof ApiClientError
                ? e.message
                : "Conversation not found or you don't have access.",
          });
        } else {
          // Transient failure mid-poll — keep the last transcript, retry.
          timer = setTimeout(() => void tick(), LIVE_POLL_MS);
        }
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  // Auto-scroll to the newest turn as it streams in (only while live, so it
  // doesn't yank a completed transcript the user is scrolling through).
  const msgCount = state.status === "ready" ? state.data.messages.length : 0;
  useEffect(() => {
    if (live && msgCount > prevCount.current) {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    prevCount.current = msgCount;
  }, [msgCount, live]);

  if (state.status === "loading") return <ReplaySkeleton />;
  if (state.status === "error") {
    return (
      <div className="space-y-4">
        <BackLink />
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </p>
      </div>
    );
  }

  const c = state.data;

  return (
    <div className="space-y-4">
      <header>
        <BackLink />
        <h1 className="mt-1 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Conversation with <span className="font-mono">{c.endCustomerRef}</span>
        </h1>
        <CopyableId id={c.id} />
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-zinc-500">
          <MetaPill>{c.channel.toUpperCase()}</MetaPill>
          <MetaPill>{c.status.replace("_", " ")}</MetaPill>
          {live && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2 py-0.5 text-[10.5px] font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
              </span>
              Live
            </span>
          )}
          {c.completed && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
              complete
            </span>
          )}
          <span>{c.turnCount} turns</span>
          <span aria-hidden="true">·</span>
          <span>
            started <time dateTime={c.startedAt}>{new Date(c.startedAt).toLocaleString()}</time>
          </span>
        </div>
        <p className="mt-1 text-[12px]">
          <Link
            to={`/agentflow/flows/${c.flowId}`}
            className="text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
          >
            Open the bound flow →
          </Link>
        </p>
      </header>

      <div className="rounded-xl border border-zinc-200 bg-white p-4 sm:p-6 dark:border-zinc-800/80 dark:bg-zinc-900/40">
        {c.messages.length === 0 ? (
          <p className="py-8 text-center text-[12.5px] text-zinc-500">
            {live ? "Waiting for the first message…" : "No messages recorded yet."}
          </p>
        ) : (
          <ol className="mx-auto max-w-4xl space-y-4">
            {c.messages.map((m, i) => (
              <li key={i}>{m.role === "tool" ? <ToolCard m={m} /> : <Bubble m={m} />}</li>
            ))}
            <li ref={endRef} aria-hidden="true" />
          </ol>
        )}
      </div>

      {c.webhookDeliveries && c.webhookDeliveries.length > 0 && (
        <div className="mt-4 rounded-xl border border-zinc-200 bg-white p-4 sm:p-6 dark:border-zinc-800/80 dark:bg-zinc-900/40">
          <h2 className="mb-3 text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
            Webhook events
            <span className="ml-2 font-normal text-zinc-500">({c.webhookDeliveries.length})</span>
          </h2>
          <ul className="mx-auto max-w-4xl space-y-1.5">
            {c.webhookDeliveries.map((d, i) => (
              <li
                key={i}
                className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-200 px-2.5 py-1.5 text-[12px] dark:border-zinc-800"
              >
                <span
                  className={
                    "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide " +
                    (d.status === "delivered"
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                      : "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300")
                  }
                >
                  {d.status}
                </span>
                <span className="font-mono text-zinc-700 dark:text-zinc-300">{d.event}</span>
                <span className="font-mono text-zinc-500">{d.type}</span>
                {d.httpStatus != null && (
                  <span className="font-mono text-zinc-500">HTTP {d.httpStatus}</span>
                )}
                {d.error && <span className="text-red-500">{d.error}</span>}
                <span className="ml-auto text-[10.5px] text-zinc-400">
                  {new Date(d.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Conversation id with click-to-copy — surfaced so it's easy to grab for
 *  debugging (e.g. the dump-agent-conversation script). */
function CopyableId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      title="Copy conversation ID"
      className="mt-1 inline-flex items-center gap-1.5 font-mono text-[11px] text-zinc-500 transition-colors hover:text-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:hover:text-zinc-200"
    >
      <span className="uppercase tracking-wider not-italic">ID</span>
      <span className="select-all">{id}</span>
      <span className="text-[10px] text-zinc-400">{copied ? "copied" : "copy"}</span>
    </button>
  );
}

function BackLink() {
  return (
    <Link
      to="/agentflow/conversations"
      className="text-[12px] text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
    >
      ← All conversations
    </Link>
  );
}

function MetaPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-wider text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-400">
      {children}
    </span>
  );
}

/** Customer (user) bubbles sit right; agent (assistant) bubbles sit left. */
function Bubble({ m }: { m: Message }) {
  const isCustomer = m.role === "user";
  return (
    <div className={"flex " + (isCustomer ? "justify-end" : "justify-start")}>
      <div className={"max-w-[80%] " + (isCustomer ? "text-right" : "text-left")}>
        <p className="mb-0.5 text-[10.5px] font-medium uppercase tracking-wider text-zinc-500">
          {isCustomer ? "Customer" : "Agent"}
        </p>
        <div
          className={
            "inline-block whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-left text-[13.5px] " +
            (isCustomer
              ? "bg-brand-50 text-brand-900 dark:bg-brand-500/10 dark:text-brand-100"
              : "bg-zinc-100 text-zinc-800 dark:bg-zinc-800/60 dark:text-zinc-200")
          }
        >
          {m.content}
        </div>
        {!isCustomer && m.stageId && (
          <p className="mt-0.5 flex flex-wrap items-center gap-1">
            <span className="inline-flex items-center gap-1 rounded bg-brand-50 px-1.5 py-0.5 font-mono text-[10px] text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
              {m.stageId}
              {m.nextStageId && m.nextStageId !== m.stageId && (
                <>
                  <span aria-hidden>→</span>
                  {m.nextStageId}
                </>
              )}
            </span>
            {m.transitionReason && (
              <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                {transitionChipText(m.transitionReason, m.transitionDetail)}
              </span>
            )}
          </p>
        )}
        {!isCustomer && m.toolCalls && m.toolCalls.length > 0 && (
          <div className="mt-1 space-y-1">
            {m.toolCalls.map((tc, i) => (
              <details
                key={tc.id || i}
                className="rounded border border-amber-200/70 bg-amber-50/50 text-[11px] dark:border-amber-500/20 dark:bg-amber-500/[0.06]"
              >
                <summary className="cursor-pointer px-2 py-1 font-mono text-amber-700 dark:text-amber-300">
                  🔧 {tc.name}({Object.keys(tc.args).join(", ")})
                </summary>
                <div className="space-y-1 border-t border-amber-200/50 px-2 py-1.5 dark:border-amber-500/15">
                  <p className="font-mono text-[10.5px] text-zinc-500">
                    args: {JSON.stringify(tc.args)}
                  </p>
                  <p className="font-mono text-[10.5px] text-zinc-700 dark:text-zinc-300">
                    → {tc.result}
                  </p>
                </div>
              </details>
            ))}
          </div>
        )}
        <p className="mt-0.5 text-[10.5px] text-zinc-500">
          <time dateTime={m.createdAt}>{new Date(m.createdAt).toLocaleString()}</time>
        </p>
      </div>
    </div>
  );
}

/** Tool call/result — collapsed by default so the conversation stays readable. */
function ToolCard({ m }: { m: Message }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-lg border border-amber-200/70 bg-amber-50/50 dark:border-amber-500/20 dark:bg-amber-500/[0.06]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-9 w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-amber-100/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 dark:hover:bg-amber-500/10"
      >
        <svg
          viewBox="0 0 16 16"
          aria-hidden="true"
          className={
            "h-3 w-3 shrink-0 text-amber-600 transition-transform duration-150 dark:text-amber-400 " +
            (open ? "rotate-90" : "")
          }
        >
          <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <span className="font-mono text-[11px] font-medium text-amber-800 dark:text-amber-300">
          {m.toolName ?? "tool"}
        </span>
        {m.toolCallId && (
          <span className="truncate font-mono text-[10.5px] text-amber-700/60 dark:text-amber-400/50">
            {m.toolCallId}
          </span>
        )}
        <time dateTime={m.createdAt} className="ml-auto shrink-0 text-[10.5px] text-zinc-500">
          {new Date(m.createdAt).toLocaleTimeString()}
        </time>
      </button>
      {open && (
        <div className="border-t border-amber-200/70 px-3 py-2 dark:border-amber-500/20">
          {m.content ? (
            <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-zinc-700 dark:text-zinc-300">
              {m.content}
            </pre>
          ) : (
            <p className="text-[12px] italic text-zinc-500">(no result body)</p>
          )}
        </div>
      )}
    </div>
  );
}

function ReplaySkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading conversation">
      <div className="space-y-2">
        <div className="h-3 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
        <div className="h-6 w-72 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
        <div className="h-3 w-56 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white p-6 dark:border-zinc-800/80 dark:bg-zinc-900/40">
        <div className="mx-auto max-w-2xl space-y-4">
          <div className="h-12 w-3/5 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800/60" />
          <div className="ml-auto h-12 w-1/2 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800/60" />
          <div className="h-12 w-3/5 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800/60" />
        </div>
      </div>
    </div>
  );
}
